import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static DEBOUNCE_TIME = 5000
  static targets = ['removeField']

  connect() {
    this._onDebouncedSubmit = this._debounceSubmit.bind(this)
    this._onVisibilityChange = this.#handleVisibilityChange.bind(this)
    this._onBeforeUnload = this.#flushPendingSave.bind(this)
    this._onBeforeVisit = this.#flushPendingSave.bind(this)
    this.element.addEventListener('change', this._onDebouncedSubmit, { passive: true })
    this.element.addEventListener('trix-change', this._onDebouncedSubmit, { passive: true })
    document.addEventListener('visibilitychange', this._onVisibilityChange)
    window.addEventListener('beforeunload', this._onBeforeUnload)
    document.addEventListener('turbo:before-visit', this._onBeforeVisit)
    this.element.addEventListener('form-file-attachment-remover:removed', this._onDebouncedSubmit)

    this.element.addEventListener('direct-upload:initialize', () => {
      this.directUploadInProgress = true
    })

    this.element.addEventListener('direct-uploads:end', () => {
      this.directUploadInProgress = false
    })

    this.element.addEventListener('turbo:submit-end', ({ detail }) => {
      if (detail.success) this.#resetRemoveFields()
    })
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    this.element.removeEventListener('change', this._onDebouncedSubmit)
    this.element.removeEventListener('trix-change', this._onDebouncedSubmit)
    document.removeEventListener('visibilitychange', this._onVisibilityChange)
    window.removeEventListener('beforeunload', this._onBeforeUnload)
    document.removeEventListener('turbo:before-visit', this._onBeforeVisit)
    this.element.removeEventListener('form-file-attachment-remover:removed', this._onDebouncedSubmit)
  }

  cancel() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = null
  }

  submit() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = null

    if (this.directUploadInProgress || this.element.hasAttribute('data-direct-uploads-processing')) {
      const handleDirectUploadsEnd = () => {
        this.element.removeEventListener('direct-uploads:end', handleDirectUploadsEnd)
        this.directUploadInProgress = false
        this.#performSubmit()
      }
      this.element.addEventListener('direct-uploads:end', handleDirectUploadsEnd, { once: true })
      return
    }

    this.#performSubmit()
  }

  #performSubmit() {
    const anchor = this.#findScrollAnchor()

    if (anchor) {
      const restoreScroll = () => {
        const newRect = anchor.element.getBoundingClientRect()
        const drift = newRect.top - anchor.offsetFromViewport
        if (Math.abs(drift) > 1) {
          window.scrollBy(0, drift)
        }
      }

      document.addEventListener('turbo:frame-render', restoreScroll, { once: true })
    }

    this.element.requestSubmit()
  }

  #findScrollAnchor() {
    const candidates = this.element.querySelectorAll('input, select, textarea, label, h1, h2, h3, [id]')

    for (const el of candidates) {
      const rect = el.getBoundingClientRect()
      if (rect.top >= 0 && rect.top < window.innerHeight) {
        return { element: el, offsetFromViewport: rect.top }
      }
    }

    return null
  }

  _debounceSubmit() {
    this._debounce(() => this.submit())
  }

  #handleVisibilityChange() {
    if (document.hidden) {
      this.#flushPendingSave(this.debounceTimer)
    }
  }

  #flushPendingSave() {
    if (!this.debounceTimer) return

    clearTimeout(this.debounceTimer)
    this.debounceTimer = null

    const formData = new FormData(this.element)
    const action = this.element.action
    navigator.sendBeacon(action, formData)
  }

  #resetRemoveFields() {
    this.removeFieldTargets.forEach(field => {
      field.type === 'checkbox' ? (field.checked = false) : (field.value = '')
    })
  }

  _debounce(callback) {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(callback, this.constructor.DEBOUNCE_TIME)
  }
}
